import {
  Box,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  withStyles,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import Pagination from '@material-ui/lab/Pagination'
import React from 'react'
import { COLORS, FONTS } from '../../constants'
import { styles as proTabStyles } from '../proTab/styles'
import {
  cycleStackSort,
  getOrderFromStack,
  SortCriterion,
} from './columnSortUtils'

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const

export type SortOrder = 'asc' | 'desc'
export type ColumnSortOrder = 'off' | 'asc' | 'desc'

export type SortablePaginatedTableColumn<
  T,
  SortField extends string = string
> = {
  id: string
  label: string
  /** When set, the column header is sortable (unless `sortable` is false). */
  sortField?: SortField
  sortable?: boolean
  renderCell: (row: T) => React.ReactNode
}

export function paginationRange(
  page: number,
  pageSize: number,
  totalCount: number
): { start: number; end: number; total: number } {
  if (totalCount <= 0) {
    return { start: 0, end: 0, total: 0 }
  }
  const safePage = Math.max(1, Math.floor(page) || 1)
  const size = Math.max(1, Math.floor(pageSize) || 1)
  const start = (safePage - 1) * size + 1
  const end = Math.min(safePage * size, totalCount)
  return { start, end, total: totalCount }
}

export function paginationSummaryLabel(
  page: number,
  pageSize: number,
  totalCount: number
): string {
  const r = paginationRange(page, pageSize, totalCount)
  return `showing ${r.start}–${r.end} of ${r.total} records`
}

export function cycleColumnSortOrder(order: ColumnSortOrder): ColumnSortOrder {
  if (order === 'off') return 'asc'
  if (order === 'asc') return 'desc'
  return 'off'
}

export function toggleSortOrder(order: SortOrder): SortOrder {
  return order === 'asc' ? 'desc' : 'asc'
}

const StyledTableRow = withStyles(() => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: COLORS.whiteStain,
    },
    '& td': {
      maxWidth: 250,
      fontFamily: FONTS.Graphik.GraphikLight,
    },
  },
}))(TableRow)

const useHeaderStyles = makeStyles(theme => ({
  sortableHeaderButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: 0,
    margin: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    '&:hover': {
      opacity: 0.85,
    },
    '&:focus-visible': {
      outline: `2px solid ${COLORS.coolTeal}`,
      outlineOffset: 2,
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  sortIcon: {
    fontSize: '1rem',
    verticalAlign: 'middle',
    marginBottom: theme.spacing(2),
    flexShrink: 0,
  },
  secondarySortBadge: {
    fontFamily: FONTS.Graphik.GraphikLight,
    fontSize: '0.65rem',
    lineHeight: 1,
    opacity: 0.75,
    marginLeft: theme.spacing(0.25),
  },
}))

type PaginationProps = {
  page: number
  pageSize: number
  totalCount: number
  pageSizeOptions?: readonly number[]
  loading?: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

type TableBodyProps<T, SortField extends string> = {
  columns: SortablePaginatedTableColumn<T, SortField>[]
  rows: T[]
  getRowKey: (row: T) => string
}

type SingleSortConfig<SortField extends string> = {
  sortMode: 'single'
  sortField: SortField
  sortOrder: SortOrder
  secondarySortField?: '' | SortField
  secondarySortOrder?: SortOrder
  enableSecondarySort?: boolean
  onSortChange: (next: {
    sortField: SortField
    sortOrder: SortOrder
    secondarySortField?: '' | SortField
    secondarySortOrder?: SortOrder
  }) => void
}

type StackSortConfig<SortField extends string> = {
  sortMode: 'stack'
  sortStack: SortCriterion<SortField>[]
  onSortChange: (sortStack: SortCriterion<SortField>[]) => void
}

export type SortablePaginatedTableProps<
  T,
  SortField extends string = string
> = PaginationProps &
  TableBodyProps<T, SortField> & {
    minWidth?: number
    tableClassName?: string
    wrapperClassName?: string
    emptyContent?: React.ReactNode
    hidePaginationWhenEmpty?: boolean
  } & (SingleSortConfig<SortField> | StackSortConfig<SortField>)

function isColumnSortable<SortField extends string>(
  column: SortablePaginatedTableColumn<unknown, SortField>
): column is SortablePaginatedTableColumn<unknown, SortField> & {
  sortField: SortField
} {
  return column.sortable !== false && column.sortField != null
}

function SortIndicator({
  order,
  priority,
}: {
  order: SortOrder | 'asc' | 'desc'
  priority?: 'primary' | 'secondary'
}) {
  const headerClasses = useHeaderStyles()
  const Icon = order === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon
  return (
    <>
      <Icon className={headerClasses.sortIcon} aria-hidden />
      {priority === 'secondary' ? (
        <span className={headerClasses.secondarySortBadge} aria-hidden>
          2
        </span>
      ) : null}
    </>
  )
}

function TablePaginationFooter({
  page,
  pageSize,
  totalCount,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  loading,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) {
  const classes = proTabStyles()
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))

  return (
    <Box className={classes.paginationFooter}>
      <Box className={classes.paginationTopRow}>
        <Typography
          variant="body2"
          component="p"
          className={classes.paginationSummary}
        >
          {paginationSummaryLabel(page, pageSize, totalCount)}
        </Typography>
        <TextField
          className={classes.paginationPageSizeField}
          select
          label="per page"
          value={pageSize}
          onChange={e => onPageSizeChange(Number(e.target.value))}
          variant="outlined"
          size="small"
          disabled={loading}
        >
          {pageSizeOptions.map(n => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box className={classes.paginationNavRow}>
        <Pagination
          count={pageCount}
          page={page}
          onChange={(_event, value) => {
            if (!loading && value !== page) {
              onPageChange(value)
            }
          }}
          color="primary"
          shape="rounded"
          showFirstButton
          showLastButton
          disabled={loading}
        />
      </Box>
    </Box>
  )
}

function SortablePaginatedTable<TRow, SortField extends string = string>(
  props: SortablePaginatedTableProps<TRow, SortField>
) {
  const {
    columns,
    rows,
    getRowKey,
    loading,
    page,
    pageSize,
    totalCount,
    pageSizeOptions,
    onPageChange,
    onPageSizeChange,
    minWidth,
    tableClassName,
    wrapperClassName,
    emptyContent,
    hidePaginationWhenEmpty = true,
  } = props

  const classes = proTabStyles()
  const headerClasses = useHeaderStyles()

  const handleHeaderClick = (
    column: SortablePaginatedTableColumn<TRow, SortField> & {
      sortField: SortField
    },
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (loading) return

    if (props.sortMode === 'stack') {
      props.onSortChange(cycleStackSort(props.sortStack, column.sortField))
      return
    }

    const {
      sortField,
      sortOrder,
      secondarySortField,
      secondarySortOrder,
    } = props

    if (props.enableSecondarySort && event.shiftKey) {
      if (column.sortField === sortField) return
      const nextSecondary =
        secondarySortField === column.sortField
          ? toggleSortOrder(secondarySortOrder ?? 'asc')
          : 'asc'
      props.onSortChange({
        sortField,
        sortOrder,
        secondarySortField: column.sortField,
        secondarySortOrder: nextSecondary,
      })
      return
    }

    if (column.sortField === sortField) {
      props.onSortChange({
        sortField,
        sortOrder: toggleSortOrder(sortOrder),
        secondarySortField,
        secondarySortOrder,
      })
      return
    }

    props.onSortChange({
      sortField: column.sortField,
      sortOrder: 'asc',
      secondarySortField:
        secondarySortField === column.sortField ? '' : secondarySortField,
      secondarySortOrder,
    })
  }

  const renderHeaderLabel = (
    column: SortablePaginatedTableColumn<TRow, SortField>
  ) => {
    if (!isColumnSortable(column)) {
      return <Typography variant="subtitle1">{column.label}</Typography>
    }

    let sortIndicator: React.ReactNode = null

    if (props.sortMode === 'stack') {
      const order = getOrderFromStack(column.sortField, props.sortStack)
      if (order === 'asc' || order === 'desc') {
        sortIndicator = <SortIndicator order={order} />
      }
    } else if (column.sortField === props.sortField) {
      sortIndicator = (
        <SortIndicator order={props.sortOrder} priority="primary" />
      )
    } else if (
      props.enableSecondarySort &&
      column.sortField === props.secondarySortField
    ) {
      sortIndicator = (
        <SortIndicator
          order={props.secondarySortOrder ?? 'asc'}
          priority="secondary"
        />
      )
    }

    const ariaSort =
      props.sortMode === 'stack'
        ? (() => {
            const o = getOrderFromStack(column.sortField, props.sortStack)
            if (o === 'asc') return 'ascending'
            if (o === 'desc') return 'descending'
            return 'none'
          })()
        : column.sortField === props.sortField
        ? props.sortOrder === 'asc'
          ? 'ascending'
          : 'descending'
        : column.sortField === props.secondarySortField
        ? props.secondarySortOrder === 'asc'
          ? 'ascending'
          : 'descending'
        : 'none'

    return (
      <button
        type="button"
        className={headerClasses.sortableHeaderButton}
        onClick={e => handleHeaderClick(column, e)}
        disabled={loading}
        aria-sort={ariaSort}
        aria-label={`sort by ${column.label}`}
      >
        <Typography variant="subtitle1" component="span">
          {column.label}
        </Typography>
        {sortIndicator}
      </button>
    )
  }

  const showPagination =
    totalCount > 0 && !(hidePaginationWhenEmpty && rows.length === 0)

  return (
    <>
      <div className={wrapperClassName ?? classes.tableWrapper}>
        <Table
          className={tableClassName ?? classes.table}
          style={minWidth != null ? { minWidth } : undefined}
        >
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.id} className={classes.tableHeader}>
                  {renderHeaderLabel(column)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <StyledTableRow key={getRowKey(row)}>
                {columns.map(column => (
                  <TableCell key={column.id}>
                    {column.renderCell(row)}
                  </TableCell>
                ))}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {rows.length === 0 && emptyContent != null ? (
        <Box mt={2}>{emptyContent}</Box>
      ) : null}

      {showPagination ? (
        <TablePaginationFooter
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          pageSizeOptions={pageSizeOptions}
          loading={loading}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      ) : null}
    </>
  )
}

export default SortablePaginatedTable
